import { Box, Checkbox, Divider, FormControlLabel, Typography, useTheme } from '@mui/material';
import { type FC, useEffect, useState } from 'react';

import { PERCENTAGE_DISCOUNT_LIMIT } from '@core/constants/discounts';
import type { ImportedDiscount } from '@core/types/api/ImportedDiscount/ImportedDiscount';
import type { QuoteProductDiscount } from '@core/types/api/QuoteProductDiscount/QuoteProductDiscount';
import {
  DiscountAmountType,
  appliedFromImported,
  appliedPercentage,
  canAddDiscount,
  discountOfferLabel,
  discountValidityLabel,
  dropsNetPriceBelowZero,
  hasSameAppliedDiscounts,
  isSelectionCombinable,
  matchesRowContext,
  reachesPercentageLimit,
  reappliedAtGross,
  settledNetPrice,
  totalDiscountOff,
} from '@core/utils/discounts';
import { hasStringValue } from '@core/utils/equality';

import { SlabButton } from '../SlabButton/SlabButton';

type Props = {
  discounts: readonly ImportedDiscount[];
  applied: readonly QuoteProductDiscount[];
  grossPrice: number;
  currencySymbol: string;
  unitLabel: string;
  readOnly: boolean;
  onApply: (applied: QuoteProductDiscount[]) => void;
  onCancel: () => void;
};

export const QuoteProductDiscountPopup: FC<Props> = ({
  discounts,
  applied,
  grossPrice,
  currencySymbol,
  unitLabel,
  readOnly,
  onApply,
  onCancel,
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState<QuoteProductDiscount[]>(() =>
    readOnly ? [...applied] : reappliedAtGross(applied, discounts, grossPrice),
  );
  useEffect(() => {
    if (!readOnly) {
      setSelected((current) => reappliedAtGross(current, discounts, grossPrice));
    }
  }, [discounts, grossPrice, readOnly]);
  // Saved discounts that are no longer offered survive only on an untouched line. Once the
  // selection changes (a pick, or an offer refreshed an amount) the draft is what the picker
  // will keep, so the popup confirms exactly that.
  const unavailable = selected.filter((d) => !matchesRowContext(d, discounts));
  const isDraftChanged = !hasSameAppliedDiscounts(selected, applied);
  const draft = isDraftChanged ? selected.filter((d) => matchesRowContext(d, discounts)) : selected;
  const isSelectionApplicable =
    !dropsNetPriceBelowZero(draft, grossPrice) &&
    appliedPercentage(draft, discounts, grossPrice) < PERCENTAGE_DISCOUNT_LIMIT &&
    isSelectionCombinable(
      draft.filter((d) => matchesRowContext(d, discounts)),
      discounts,
    );

  const selectedIDs = selected.map((d) => d.importedDiscountId);
  const unselect = (discount: QuoteProductDiscount): void =>
    setSelected(selected.filter((d) => d.importedDiscountId !== discount.importedDiscountId));
  const groups = [
    { label: 'Stackable', items: discounts.filter((d) => d.stackable) },
    { label: 'Single', items: discounts.filter((d) => !d.stackable) },
  ].filter((group) => group.items.length > 0);

  const toggle = (discount: ImportedDiscount): void => {
    setSelected(
      selectedIDs.includes(discount.id)
        ? selected.filter((d) => d.importedDiscountId !== discount.id)
        : [...selected, appliedFromImported(discount, grossPrice)],
    );
  };

  return (
    <Box
      sx={{
        width: '31.375rem',
        maxWidth: '100%',
        backgroundColor: theme.palette.SlabGray[0],
        border: `1px solid ${theme.palette.SlabGray.Stroke}`,
        borderRadius: '0.5rem',
        boxShadow: theme.shadows[8],
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          padding: '0.75rem',
          borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
        }}
      >
        <Typography
          sx={{ fontSize: '1.25rem', fontWeight: 500, color: theme.palette.SlabGray[800] }}
        >
          {`Eligible discounts (${discounts.length})`}
        </Typography>
        <Typography sx={{ color: theme.palette.SlabGray[600] }}>
          Based on buyer, plant, product & delivery type
        </Typography>
      </Box>
      <Box sx={{ maxHeight: '26rem', overflowY: 'auto', paddingY: '0.75rem' }}>
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: theme.palette.SlabGray[600],
            paddingX: '0.75rem',
            paddingBottom: '0.875rem',
          }}
        >
          Single discounts apply alone - picking one blocks every other discount on this product.
          Stackable discounts combine freely with each other.
        </Typography>
        {groups.map((group) => (
          <Box key={group.label} role='group' aria-label={group.label}>
            <Typography
              sx={{
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                color: theme.palette.SlabGray[600],
                backgroundColor: theme.palette.SlabGray.TableHover,
                borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
                padding: '0.5rem 0.75rem',
              }}
            >
              {group.label}
            </Typography>
            {group.items.map((discount) => {
              const isSelected = selectedIDs.includes(discount.id);
              const dropsBelowZero =
                !readOnly &&
                !isSelected &&
                dropsNetPriceBelowZero(
                  [...draft, appliedFromImported(discount, grossPrice)],
                  grossPrice,
                );
              const needsPrice =
                !isSelected &&
                discount.amountType === DiscountAmountType.Percentage &&
                grossPrice <= 0;
              const isBlocked =
                !isSelected &&
                (dropsBelowZero ||
                  needsPrice ||
                  !canAddDiscount(discount, draft, discounts) ||
                  reachesPercentageLimit(discount, draft, discounts, grossPrice));
              const blockedDetail = dropsBelowZero
                ? "Can't apply: net price would drop below zero"
                : "Can't apply: the line needs a unit price";
              const detail =
                dropsBelowZero || needsPrice
                  ? blockedDetail
                  : discountValidityLabel(discount.validTo);

              return (
                <FormControlLabel
                  key={discount.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    margin: 0,
                    gap: '0.75rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: isBlocked
                      ? theme.palette.SlabGray[100]
                      : theme.palette.SlabGray[0],
                    ...(isSelected && { backgroundColor: theme.palette.SlabIndigo[50] }),
                    '&:hover': isBlocked
                      ? undefined
                      : { backgroundColor: theme.palette.SlabIndigo[50] },
                    '& .MuiFormControlLabel-label': { flex: 1 },
                  }}
                  control={
                    <Checkbox
                      sx={{ padding: 0 }}
                      disabled={readOnly || isBlocked}
                      checked={isSelected}
                      onChange={() => toggle(discount)}
                    />
                  }
                  label={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'space-between',
                        gap: '0.75rem',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                        <Typography
                          sx={{
                            color: isBlocked
                              ? theme.palette.SlabGray[500]
                              : theme.palette.SlabGray[900],
                          }}
                        >
                          {discount.name}
                        </Typography>
                        {hasStringValue(detail) && (
                          <Typography
                            sx={{
                              fontSize: '0.875rem',
                              color: isBlocked
                                ? theme.palette.SlabGray[400]
                                : theme.palette.SlabGray[500],
                            }}
                          >
                            {detail}
                          </Typography>
                        )}
                      </Box>
                      <Typography
                        sx={{
                          flexShrink: 0,
                          fontSize: '1.125rem',
                          color: isBlocked
                            ? theme.palette.SlabBlue[200]
                            : theme.palette.SlabBlue[500],
                        }}
                      >
                        {discountOfferLabel(discount, currencySymbol, unitLabel)}
                      </Typography>
                    </Box>
                  }
                />
              );
            })}
          </Box>
        ))}
        {unavailable.length > 0 && (
          <Box role='group' aria-label='No longer offered'>
            <Typography
              sx={{
                fontSize: '0.875rem',
                textTransform: 'uppercase',
                color: theme.palette.SlabGray[600],
                backgroundColor: theme.palette.SlabGray.TableHover,
                borderBottom: `1px solid ${theme.palette.SlabGray.Stroke}`,
                padding: '0.5rem 0.75rem',
              }}
            >
              No longer offered
            </Typography>
            {unavailable.map((discount) => (
              <FormControlLabel
                key={discount.importedDiscountId}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  margin: 0,
                  gap: '0.75rem',
                  padding: '0.5rem 0.75rem',
                  backgroundColor: theme.palette.SlabIndigo[50],
                  '& .MuiFormControlLabel-label': { flex: 1 },
                }}
                control={
                  <Checkbox
                    sx={{ padding: 0 }}
                    disabled={readOnly || isDraftChanged}
                    checked={!isDraftChanged}
                    onChange={() => unselect(discount)}
                  />
                }
                label={
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: '0.75rem',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <Typography sx={{ color: theme.palette.SlabGray[900] }}>
                        {discount.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.875rem', color: theme.palette.SlabGray[500] }}>
                        {isDraftChanged
                          ? 'Removed on apply: the line changed and this is no longer offered'
                          : 'Saved on this line; uncheck to remove it'}
                      </Typography>
                    </Box>
                    <Typography
                      sx={{
                        flexShrink: 0,
                        fontSize: '1.125rem',
                        color: theme.palette.SlabBlue[500],
                      }}
                    >
                      {`-${currencySymbol}${parseFloat(discount.appliedAmount || '0').toFixed(2)}${unitLabel}`}
                    </Typography>
                  </Box>
                }
              />
            ))}
          </Box>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          padding: '0.75rem',
          backgroundColor: theme.palette.SlabIndigo[50],
          borderTop: `1px solid ${theme.palette.SlabGray.Stroke}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.25rem',
            '& .MuiTypography-root': { color: theme.palette.SlabGray[600] },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>Gross price</Typography>
            <Typography>{`${currencySymbol}${grossPrice.toFixed(2)}`}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography>{`Discounts applied (${draft.length})`}</Typography>
            <Typography>{`-${currencySymbol}${totalDiscountOff(draft).toFixed(2)}`}</Typography>
          </Box>
        </Box>
        <Divider sx={{ borderColor: theme.palette.SlabGray.Stroke }} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            '& .MuiTypography-root': {
              fontSize: '1.125rem',
              fontWeight: 500,
              color: theme.palette.SlabGray[950],
            },
          }}
        >
          <Typography>Net price</Typography>
          <Typography>
            {`${currencySymbol}${settledNetPrice(draft, grossPrice).toFixed(2)}`}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '1rem', paddingTop: '0.25rem' }}>
          <SlabButton
            variant='slab-secondary'
            sx={{
              flex: 1,
              padding: '0.875rem 1rem',
              backgroundColor: theme.palette.SlabGray[0],
              '&:hover': { backgroundColor: theme.palette.SlabGray[0] },
            }}
            onClick={onCancel}
          >
            Cancel
          </SlabButton>
          <SlabButton
            variant='slab-primary'
            sx={{ flex: 1, padding: '0.875rem 1rem' }}
            disabled={readOnly || !isDraftChanged || !isSelectionApplicable}
            onClick={() => onApply(draft)}
          >
            Apply
          </SlabButton>
        </Box>
      </Box>
    </Box>
  );
};
